// React
import { useEffect, useState, useCallback } from "react";

// Third party imports
import { useDispatch, useSelector } from "react-redux";

// Redux
import { setError } from "../../redux/slices/snackbarSlice";

/**
 * Generic hook for fetching entity details
 * @param {string} entityId - ID of the entity to fetch
 * @param {boolean} enabled - Whether the fetch should be enabled
 * @param {Object} config - Configuration object containing:
 *   - fetchFn: API function to fetch the entity
 *   - selectFn: Redux selector function
 *   - setActionFn: Redux action to set selected entity
 *   - clearActionFn: Redux action to clear selected entity
 *   - entityName: Name of the entity for error messages
 * @returns {Object} Object containing entity from store, loading state, error and refetch function
 */
const useEntity = (entityId, enabled = true, config) => {
  const dispatch = useDispatch();
  const {
    fetchFn,
    selectFn,
    setActionFn,
    clearActionFn,
    entityName = "entity"
  } = config;

  // Redux selectors
  const selectedEntity = useSelector(selectFn);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setErrorState] = useState(null);

  // Track if we've already fetched this entity to prevent duplicates
  const [fetchedEntityId, setFetchedEntityId] = useState(null);

  /**
   * Fetch entity from API and store in Redux
   */
  const fetchEntity = useCallback(async () => {
    if (!entityId || !enabled) return;

    // Prevent duplicate requests for the same entity
    if (fetchedEntityId === entityId && selectedEntity?.id === entityId) {
      return;
    }

    setIsLoading(true);
    setErrorState(null);

    try {
      const response = await fetchFn(entityId);

      // Save entity to Redux store
      dispatch(setActionFn(response.data));

      // Mark this entity as fetched
      setFetchedEntityId(entityId);

    } catch (err) {
      setErrorState(err);
      dispatch(setError({
        message: err.response?.data?.message || `Failed to fetch ${entityName} details`,
        severity: "error"
      }));
    } finally {
      setIsLoading(false);
    }
  }, [entityId, enabled, dispatch, fetchedEntityId, selectedEntity?.id, fetchFn, setActionFn, entityName]);

  // Fetch entity when entityId or enabled changes
  useEffect(() => {
    fetchEntity();
  }, [fetchEntity]);

  // Clear selected entity when component unmounts or entityId changes
  useEffect(() => {
    return () => {
      if (!enabled && clearActionFn) {
        dispatch(clearActionFn());
      }
    };
  }, [enabled, dispatch, clearActionFn]);

  // Reset fetched ID when entityId changes
  useEffect(() => {
    if (entityId !== fetchedEntityId) {
      setFetchedEntityId(null);
    }
  }, [entityId, fetchedEntityId]);

  return {
    [entityName]: selectedEntity,
    isLoading,
    error,
    refetch: fetchEntity
  };
};

export default useEntity;
